import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ThreeCanvas from '../components/ThreeCanvas';
import SummaryPanel from '../components/SummaryPanel';
import { Layers, ArrowLeft, Eye, Grid3X3 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AA_LOGO = '/aa-logo.png';

export default function SharedDesign() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('3d');
  const [selectedInstance, setSelectedInstance] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [dRes, cRes] = await Promise.all([
          axios.get(`${API}/designs/shared/${shareId}`),
          axios.get(`${API}/components`)
        ]);
        setDesign(dRes.data);
        setComponents(cRes.data);
      } catch {
        setError('Design not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#020617]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !design) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#020617]" data-testid="shared-error">
        <p className="text-slate-400 mb-4">{error || 'Design not found'}</p>
        <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white">Go Home</Button>
      </div>
    );
  }

  const placedComponents = design.placed_components || [];
  
  const totalCost = placedComponents.reduce((sum, pc) => {
    const comp = components.find(c => c.comp_id === pc.comp_id);
    return sum + (comp ? comp.price : 0);
  }, 0);

  const totalWeight = placedComponents.reduce((sum, pc) => {
    const comp = components.find(c => c.comp_id === pc.comp_id);
    return sum + (comp ? comp.weight : 0);
  }, 0);

  // Get selected component info
  const selectedComponent = selectedInstance 
    ? placedComponents.find(pc => pc.instance_id === selectedInstance)
    : null;
  const selectedCompInfo = selectedComponent 
    ? components.find(c => c.comp_id === selectedComponent.comp_id)
    : null;

  return (
    <div className="h-screen flex flex-col bg-[#020617]" data-testid="shared-design-page">
      {/* Header */}
      <header className="h-14 glass-panel flex items-center justify-between px-4 z-40 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" />
          </Button>
          <img src={AA_LOGO} alt="AA" className="h-7" />
          <div className="w-px h-6 bg-white/10" />
          <Layers className="w-5 h-5 text-blue-500" />
          <span className="font-chivo font-bold text-white">{design.name}</span>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">Shared View</span>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center bg-slate-800/50 rounded-lg p-0.5">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setViewMode('2d')}
            className={`h-7 px-2 text-xs ${viewMode === '2d' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="shared-view-2d-btn"
          >
            <Grid3X3 className="w-3.5 h-3.5 mr-1" />
            2D
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setViewMode('3d')}
            className={`h-7 px-2 text-xs ${viewMode === '3d' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="shared-view-3d-btn"
          >
            <Eye className="w-3.5 h-3.5 mr-1" />
            3D
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <ThreeCanvas
            viewMode={viewMode}
            components={components}
            placedComponents={placedComponents}
            onDrop={() => {}}
            onMove={() => {}}
            onRemove={() => {}}
            selectedInstance={selectedInstance}
            setSelectedInstance={setSelectedInstance}
            moveMode={null}
            setMoveMode={() => {}}
            validPlacements={[]}
            customMaterials={{}}
          />
        </main>

        {/* Right Panel */}
        <aside className="w-80 border-l border-white/5 bg-slate-900/40 flex flex-col">
          {/* Component Info on Hover/Select */}
          {selectedCompInfo && (
            <div className="p-4 border-b border-white/5 bg-blue-500/10">
              <h3 className="font-chivo font-semibold text-white text-sm mb-2">{selectedCompInfo.name}</h3>
              <p className="text-xs text-slate-400 mb-3">{selectedCompInfo.description}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-800/50 rounded p-2">
                  <span className="text-slate-500 block">Category</span>
                  <span className="text-white">{selectedCompInfo.category}</span>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <span className="text-slate-500 block">Size</span>
                  <span className="text-white">{selectedCompInfo.grid_w}x{selectedCompInfo.grid_h}</span>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <span className="text-slate-500 block">Price</span>
                  <span className="text-blue-400">₹{selectedCompInfo.price.toLocaleString('en-IN')}</span>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <span className="text-slate-500 block">Weight</span>
                  <span className="text-white">{selectedCompInfo.weight}kg</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-600 mt-3 text-center">
                Position: ({selectedComponent.x}, {selectedComponent.y})
              </p>
            </div>
          )}

          {!selectedCompInfo && (
            <div className="p-4 border-b border-white/5 text-center text-slate-500 text-xs">
              Click on a component to see details
            </div>
          )}

          {/* Summary */}
          <SummaryPanel
            components={components}
            placedComponents={placedComponents}
            totalCost={totalCost}
            totalWeight={totalWeight}
            onRemove={() => {}}
            readOnly={true}
          />
        </aside>
      </div>
    </div>
  );
}
